<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue'

import { Check } from 'lucide-vue-next'

import BaseButton from '#.shared/components/buttons/BaseButton.vue'

import { PRESET_PALETTE } from '../../presetPalette'

/*
| A preset's colour (STG-WEB-15): a swatch that opens the suggested palette,
| with a custom picker for anything else. The value is "#RRGGBB", as the API
| takes it.
*/

const model = defineModel<string>({ required: true })

const { label = 'Colour' } = defineProps<{
  label?: string
}>()

const popover = useTemplateRef('popover')

const selected = computed(() => model.value.toUpperCase())
const suggestion = computed(() =>
  PRESET_PALETTE.find((color) => color.hex === selected.value),
)

const isCustomOpen = ref(false)

// PrimeVue's ColorPicker speaks hex without the "#"
const pickerValue = computed({
  get: () => model.value.replace(/^#/, ''),
  set: (value: string) => {
    model.value = `#${value.replace(/^#/, '').toUpperCase()}`
  },
})

function toggle(event: Event) {
  // A colour from outside the palette opens on the custom picker
  isCustomOpen.value = model.value !== '' && suggestion.value === undefined
  popover.value?.toggle(event)
}

function choose(hex: string) {
  model.value = hex
  popover.value?.hide()
}
</script>

<template>
  <button
    type="button"
    class="flex size-9 shrink-0 items-center justify-center rounded-md border border-edge bg-white focus-visible:ring-2 focus-visible:ring-accent-500 focus-visible:outline-none dark:bg-transparent"
    :aria-label="`${label}: ${suggestion?.name ?? (model || 'none')}`"
    aria-haspopup="dialog"
    data-testid="preset-color-select"
    @click="toggle"
  >
    <span
      class="size-5 rounded-sm border border-edge"
      :style="{ background: model || 'transparent' }"
      aria-hidden="true"
    />
  </button>

  <Popover ref="popover" :aria-label="label">
    <div class="flex w-56 flex-col gap-3">
      <div
        role="group"
        aria-label="Suggested colours"
        class="grid grid-cols-6 gap-2"
      >
        <button
          v-for="color in PRESET_PALETTE"
          :key="color.hex"
          type="button"
          class="flex size-7 items-center justify-center rounded-sm focus-visible:ring-2 focus-visible:ring-accent-500 focus-visible:ring-offset-1 focus-visible:outline-none"
          :style="{ background: color.hex }"
          :aria-label="color.name"
          :aria-pressed="color.hex === selected"
          @click="choose(color.hex)"
        >
          <Check
            v-if="color.hex === selected"
            class="size-4 text-white"
            aria-hidden="true"
          />
        </button>
      </div>

      <BaseButton
        label="Custom colour"
        size="sm"
        variant="text"
        severity="secondary"
        :pressed="isCustomOpen"
        @click="isCustomOpen = !isCustomOpen"
      />

      <ColorPicker
        v-if="isCustomOpen"
        v-model="pickerValue"
        inline
        format="hex"
        aria-label="Custom colour"
      />
    </div>
  </Popover>
</template>
